import { useLingui } from '@lingui/react';
import { ArrowUpRight, Users } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import {
  responsibleDetail,
  responsibleInitials,
  responsibleName,
  type Responsible,
} from '@/types/workflowResponsible';

interface ResponsibleCellProps {
  responsible: Responsible;
  /** Named for an OVERDUE item: who it escalates to. */
  escalationTargetName?: string;
  /** Compact: one line, for cards. Default: avatar, name and the second line. */
  compact?: boolean;
  className?: string;
  'data-testid'?: string;
  /** Test id of the escalation line; defaults to `<data-testid>-escalates`. */
  escalationTestId?: string;
}

/**
 * Who has to act on a workflow item: a person (avatar and name), the group
 * lead of the child's group, or the role the item waits for. An overdue item
 * also names the person it escalates to.
 */
export function ResponsibleCell({
  responsible,
  escalationTargetName,
  compact = false,
  className,
  'data-testid': testId,
  escalationTestId,
}: ResponsibleCellProps) {
  const { i18n } = useLingui();
  const name = responsibleName(i18n, responsible);
  const detail = responsibleDetail(i18n, responsible);
  const initials = responsibleInitials(responsible);
  const isPerson = responsible.kind === 'person' || responsible.kind === 'lead';
  const waiting = responsible.kind === 'role' || responsible.kind === 'nobody';

  if (compact) {
    return (
      <div className={cn('flex items-center gap-1.5 min-w-0 text-xs', className)} data-testid={testId}>
        {isPerson ? (
          <Avatar className="h-5 w-5 shrink-0">
            <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
          </Avatar>
        ) : (
          <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        )}
        <span className={cn('truncate', waiting ? 'text-muted-foreground' : 'font-medium')} title={detail ?? name}>
          {name}
        </span>
        {responsible.kind === 'lead' && responsible.groupName && (
          <span className="truncate text-muted-foreground">· {responsible.groupName}</span>
        )}
      </div>
    );
  }

  return (
    <div className={cn('flex items-start gap-2 min-w-0', className)} data-testid={testId}>
      {isPerson ? (
        <Avatar className="h-7 w-7 shrink-0 mt-0.5">
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
      ) : (
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground/50 mt-0.5"
          aria-hidden="true"
        >
          <Users className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
      )}
      <div className="min-w-0 leading-tight">
        <div className={cn('text-sm truncate', waiting ? 'text-muted-foreground' : 'font-medium')} title={name}>
          {name}
        </div>
        {detail && (
          <div className="text-xs text-muted-foreground line-clamp-2 break-words" title={detail}>
            {detail}
          </div>
        )}
        {escalationTargetName && (
          <div
            className="flex items-start gap-1 text-xs text-destructive whitespace-normal"
            data-testid={escalationTestId ?? (testId ? `${testId}-escalates` : undefined)}
          >
            <ArrowUpRight className="h-3 w-3 mt-0.5 shrink-0" aria-hidden="true" />
            {/* Wraps instead of truncating: the name is what the line is for. */}
            <span className="min-w-0 break-words">
              {i18n._({ id: 'workflow.detail.escalatesTo', message: 'Escalates to' })}:{' '}
              <span className="font-medium">{escalationTargetName}</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
